/**
 * The trainer's own writes to a clip's thread, applied to the cached pages optimistically and
 * undone ONE AT A TIME. Three writes can be in flight together (a post, an edit, a delete), and a
 * rollback that restored a snapshot taken before the first would also undo the other two.
 *
 * Pages are newest first and each page is oldest first, as the thread read pages them; a new
 * comment lands at the end of page 0.
 */

import type { ClipComment } from '~/entities/exercise-clip';

export type ThreadPages = readonly (readonly ClipComment[])[];

function placeOf(
  pages: ThreadPages,
  id: number,
): { readonly page: number; readonly index: number } | null {
  for (const [page, entries] of pages.entries()) {
    const index = entries.findIndex((entry) => entry.id === id);
    if (index !== -1) return { page, index };
  }
  return null;
}

export function appendComment(pages: ThreadPages, comment: ClipComment): ThreadPages {
  if (pages.length === 0) return [[comment]];
  return pages.map((entries, page) => (page === 0 ? [...entries, comment] : entries));
}

export function removeComment(pages: ThreadPages, id: number): ThreadPages {
  return pages.map((entries) => entries.filter((entry) => entry.id !== id));
}

export function replaceComment(
  pages: ThreadPages,
  id: number,
  change: (comment: ClipComment) => ClipComment,
): ThreadPages {
  return pages.map((entries) => entries.map((entry) => (entry.id === id ? change(entry) : entry)));
}

/**
 * The server's answer to a post replaces the temporary comment in place. A refetch that landed
 * while the post was in flight may already carry the confirmed comment — then the temporary one
 * simply goes — or may have replaced the thread without either, and then the confirmed comment is
 * appended rather than lost.
 */
export function confirmComment(
  pages: ThreadPages,
  temporaryId: number,
  confirmed: ClipComment,
): ThreadPages {
  if (placeOf(pages, confirmed.id) !== null) return removeComment(pages, temporaryId);
  if (placeOf(pages, temporaryId) !== null) {
    return replaceComment(pages, temporaryId, () => confirmed);
  }
  return appendComment(pages, confirmed);
}

/**
 * A delete that failed puts the comment back in date order, unless a refetch already did. By date
 * rather than by the place it was taken from: the timeline's copy of the newest page and the
 * thread's own pages hold different slices of the same thread.
 */
export function restoreComment(pages: ThreadPages, comment: ClipComment): ThreadPages {
  if (placeOf(pages, comment.id) !== null) return pages;
  if (pages.length === 0) return [[comment]];
  const found = pages.findIndex(
    (entries) => entries.length === 0 || (entries[0]?.createdAt ?? 0) <= comment.createdAt,
  );
  const target = found === -1 ? pages.length - 1 : found;
  return pages.map((entries, index) => {
    if (index !== target) return entries;
    const at = entries.findIndex((entry) => entry.createdAt > comment.createdAt);
    return at === -1 ? [...entries, comment] : [...entries.slice(0, at), comment, ...entries.slice(at)];
  });
}

/**
 * The thread as drawn, with the comments the trainer sent before it had been read. Those are held
 * outside the cache: written into it, they would stand in for a thread nobody has read yet, and a
 * failed post would then leave it looking empty. Until the read lands they are all there is;
 * afterwards each one shows once, from the thread when the thread already has it.
 */
export function withEarlyComments(
  thread: readonly ClipComment[] | null,
  early: readonly ClipComment[],
): readonly ClipComment[] {
  if (thread === null) return early;
  const known = new Set(thread.map((entry) => entry.id));
  return [...thread, ...early.filter((entry) => !known.has(entry.id))];
}
